/**
 * Shared display formatters.
 *
 * Single source of truth for money and date rendering. Services and pages must
 * import from here rather than re-implementing: three separate date formatters
 * with three different behaviours used to exist, and only one of them handled
 * the shapes that actually reach the portal.
 */

/**
 * Format a price given in cents as USD.
 * @param cents - Price in cents (Stripe amounts are always in cents)
 */
export function formatPrice(cents: number): string {
	const dollars = cents / 100;
	return new Intl.NumberFormat('en-US', {
		style: 'currency',
		currency: 'USD'
	}).format(dollars);
}

/**
 * Format a number as USD currency without decimals.
 */
export function formatCurrency(amount: number): string {
	return new Intl.NumberFormat('en-US', {
		style: 'currency',
		currency: 'USD',
		minimumFractionDigits: 0,
		maximumFractionDigits: 0
	}).format(amount);
}

/**
 * Format a number as a percentage with configurable decimal places.
 */
export function formatPercentage(value: number, decimals: number = 2): string {
	return `${value.toFixed(decimals)}%`;
}

/**
 * Format a date for display as "January 5, 2026".
 *
 * Handles every shape a date arrives in on this client:
 *  - Firestore Timestamp (has .toDate())
 *  - serialized Firestore Timestamp from a callable ({ _seconds })
 *  - ISO string / epoch number (callable responses)
 *  - Date
 *
 * Returns 'N/A' for null/undefined/unparseable input rather than the
 * "Invalid Date" string that `new Date(x)` produces.
 */
export function formatDate(value: unknown): string {
	if (value === null || value === undefined || value === '') return 'N/A';

	let date: Date;
	const candidate = value as { toDate?: () => Date; _seconds?: number };

	if (typeof candidate?.toDate === 'function') {
		date = candidate.toDate();
	} else if (typeof candidate?._seconds === 'number') {
		// Serialized Firestore Timestamp (callable/JSON boundary)
		date = new Date(candidate._seconds * 1000);
	} else if (value instanceof Date) {
		date = value;
	} else if (typeof value === 'string' || typeof value === 'number') {
		date = new Date(value);
	} else {
		return 'N/A';
	}

	if (isNaN(date.getTime())) {
		return 'N/A';
	}

	return date.toLocaleDateString('en-US', {
		year: 'numeric',
		month: 'long',
		day: 'numeric'
	});
}
